import {
  NumericTextBox,
  NumericTextBoxBlurEvent,
  NumericTextBoxChangeEvent,
  NumericTextBoxFocusEvent,
} from '@progress/kendo-react-inputs';
import { NumberFormatOptions } from '@progress/kendo-react-intl';
import clsx from 'clsx';
import { omit } from 'lodash';
import { HTMLAttributes, SyntheticEvent, useCallback, useEffect, useMemo, useState } from 'react';
import useDebounceCallback from 'src/hooks/useDebounceCallback';

interface NumberStepperProps extends Omit<HTMLAttributes<HTMLDivElement>, 'onChange'> {
  value: number;
  onChange: (e: SyntheticEvent, value: number) => void;
  disabled?: boolean;
  maxValue?: number;
  minValue?: number;
  /** How long the value must stay unchanged before `onChange` fires; each change restarts it. */
  debounceMs?: number;
}

const DECIMAL_PLACES = 2;
const ABSOLUTE_MAX = 9999;
const ABSOLUTE_MIN = -9999;
const VALID_INPUT_PATTERN = /^-?\d*\.?\d{0,2}$/;

const NUMBER_FORMAT: NumberFormatOptions = {
  style: 'decimal',
  useGrouping: false,
  minimumFractionDigits: 0,
  maximumFractionDigits: DECIMAL_PLACES,
};

const clampValue = (value: number, minValue: number, maxValue: number) =>
  Math.min(maxValue, Math.max(minValue, value));

const resolveValue = (value: number | null | undefined) =>
  value == null || !Number.isFinite(value) ? 0 : value;

const isSignToggleInput = (value: string) => /^-?\d*\.?\d*-$/.test(value);

const isIntermediateInput = (value: string) =>
  value === '' ||
  value === '-' ||
  value === '.' ||
  value === '-.' ||
  /^-?\d+\.$/.test(value) ||
  isSignToggleInput(value);

const isOutOfRangeInput = (candidate: string, minValue: number, maxValue: number) => {
  if (isIntermediateInput(candidate)) {
    return false;
  }
  if (!VALID_INPUT_PATTERN.test(candidate)) {
    return true;
  }
  const parsed = Number(candidate);
  return Number.isFinite(parsed) && (parsed > maxValue || parsed < minValue);
};

const NumberStepper = ({
  value,
  onChange,
  disabled = false,
  maxValue = ABSOLUTE_MAX,
  minValue = ABSOLUTE_MIN,
  debounceMs = 500,
  ...other
}: NumberStepperProps) => {
  const effectiveMax = Math.min(maxValue, ABSOLUTE_MAX);
  const effectiveMin = Math.max(minValue, ABSOLUTE_MIN);
  const [localValue, setLocalValue] = useState(
    clampValue(resolveValue(value), effectiveMin, effectiveMax)
  );
  const [isFocused, setIsFocused] = useState(false);
  const [inputKey, setInputKey] = useState(0);

  const disableDecrease = localValue <= effectiveMin;
  const disableIncrease = localValue >= effectiveMax;
  const inputResetKey = useMemo(
    () => `${inputKey}-${effectiveMin}-${effectiveMax}`,
    [inputKey, effectiveMin, effectiveMax]
  );

  useEffect(() => {
    setLocalValue(clampValue(resolveValue(value), effectiveMin, effectiveMax));
  }, [value, effectiveMin, effectiveMax]);

  const [debouncedOnChange] = useDebounceCallback(onChange, debounceMs);

  const commitValue = useCallback(
    (event: SyntheticEvent, nextValue: number) => {
      const normalized = nextValue === 0 ? 0 : clampValue(nextValue, effectiveMin, effectiveMax);
      setLocalValue(normalized);
      debouncedOnChange?.(event, normalized);
      return normalized;
    },
    [debouncedOnChange, effectiveMax, effectiveMin]
  );

  const handleChange = useCallback(
    (event: NumericTextBoxChangeEvent) => {
      event.syntheticEvent?.stopPropagation();
      if (disabled) {
        return;
      }
      const raw = (event.syntheticEvent?.target as HTMLInputElement | undefined)?.value ?? '';
      const nextValue = resolveValue(event.value);
      commitValue(event.syntheticEvent, nextValue);
      if (
        isFocused &&
        raw &&
        !isIntermediateInput(raw) &&
        isOutOfRangeInput(raw, effectiveMin, effectiveMax)
      ) {
        setInputKey((key) => key + 1);
      }
    },
    [commitValue, disabled, effectiveMax, effectiveMin, isFocused]
  );

  const handleIncrease = useCallback(
    (event: SyntheticEvent) => {
      event.stopPropagation();
      commitValue(event, Math.min(localValue + 1, effectiveMax));
    },
    [commitValue, effectiveMax, localValue]
  );

  const handleDecrease = useCallback(
    (event: SyntheticEvent) => {
      event.stopPropagation();
      commitValue(event, Math.max(localValue - 1, effectiveMin));
    },
    [commitValue, effectiveMin, localValue]
  );

  const handleFocus = useCallback(
    (event: NumericTextBoxFocusEvent) => {
      if (disabled) {
        return;
      }
      setIsFocused(true);
      event.target.element?.select();
    },
    [disabled]
  );

  const handleBlur = useCallback(
    (event: NumericTextBoxBlurEvent) => {
      setIsFocused(false);
      const current = resolveValue(event.target.value);
      const clamped = current === 0 ? 0 : clampValue(current, effectiveMin, effectiveMax);
      if (clamped !== localValue) {
        commitValue(event.syntheticEvent, clamped);
      }
    },
    [commitValue, effectiveMax, effectiveMin, localValue]
  );

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (disabled || event.ctrlKey || event.metaKey || event.altKey || event.key.length !== 1) {
        return;
      }
      const input = event.target as HTMLInputElement;
      if (input.getAttribute('aria-label') !== 'Quantity') {
        return;
      }
      // Kendo NumericTextBox toggles polarity when "-" is typed at the end (or anywhere).
      // Let Kendo NumericTextBox toggle +/− each time "-" is pressed.
      if (event.key === '-') {
        return;
      }
      const selectionStart = input.selectionStart ?? 0;
      const selectionEnd = input.selectionEnd ?? 0;
      const nextValue = `${input.value.slice(0, selectionStart)}${event.key}${input.value.slice(
        selectionEnd
      )}`;
      if (isOutOfRangeInput(nextValue, effectiveMin, effectiveMax)) {
        event.preventDefault();
        event.stopPropagation();
      }
    },
    [disabled, effectiveMax, effectiveMin]
  );

  return (
    <div
      className={clsx(
        'flex items-center justify-between border border-gray-300 rounded-md shadow-sm',
        isFocused && !disabled && 'ring',
        disabled && 'bg-gray-100',
        !disabled && 'bg-white',
        other.className
      )}
      onChange={(event) => event.stopPropagation()}
      onKeyDown={handleKeyDown}
      {...omit(other, 'className')}
    >
      <button
        type="button"
        onClick={handleDecrease}
        disabled={disableDecrease || disabled}
        className={clsx(
          'flex items-center justify-center px-1 py-1 bg-transparent border-0 cursor-pointer',
          disabled || disableDecrease
            ? 'text-gray-400 cursor-not-allowed'
            : 'text-blue-400 hover:text-blue-600'
        )}
        aria-label="Decrease quantity"
      >
        <span className="text-lg font-normal leading-none mt-[-2px]">−</span>
      </button>
      <NumericTextBox
        key={inputResetKey}
        value={localValue}
        min={effectiveMin}
        max={effectiveMax}
        step={1}
        format={NUMBER_FORMAT}
        spinners={false}
        disabled={disabled}
        inputType="text"
        fillMode={null}
        rounded={null}
        size="small"
        width="60%"
        ariaLabel="Quantity"
        onChange={handleChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        className={clsx(
          '!border-0 !bg-transparent !shadow-none',
          disabled ? 'text-gray-400' : 'text-black'
        )}
        inputStyle={{
          textAlign: 'center',
          background: 'transparent',
          border: 0,
          outline: 'none',
          cursor: disabled ? 'not-allowed' : 'text',
        }}
      />
      <button
        type="button"
        onClick={handleIncrease}
        disabled={disabled || disableIncrease}
        className={clsx(
          `flex items-center justify-center px-1 py-1 bg-transparent border-0 cursor-pointer  ${
            disabled || disableIncrease
              ? 'text-gray-400 cursor-not-allowed'
              : 'text-blue-400 hover:text-blue-600'
          }`
        )}
        aria-label="Increase quantity"
      >
        <span className="text-lg font-normal leading-none mt-[-2px]">+</span>
      </button>
    </div>
  );
};

export default NumberStepper;
